import { useState, useEffect } from 'react'

export interface PricingData {
  countryCode: string
  salePrice: number
  comparePrice: number
  currencyCode: string
  symbol: string
}

export const PRICING_CONFIG: Record<string, PricingData> = {
  CL: {
    countryCode: 'CL',
    salePrice: 8699,
    comparePrice: 32326,
    currencyCode: 'CLP',
    symbol: '$',
  },
  AR: {
    countryCode: 'AR',
    salePrice: 13899,
    comparePrice: 51500,
    currencyCode: 'ARS',
    symbol: '$',
  },
  CO: {
    countryCode: 'CO',
    salePrice: 37499,
    comparePrice: 139200,
    currencyCode: 'COP',
    symbol: '$',
  },
  MX: {
    countryCode: 'MX',
    salePrice: 179.97,
    comparePrice: 650,
    currencyCode: 'MXN',
    symbol: '$',
  },
  UY: {
    countryCode: 'UY',
    salePrice: 399,
    comparePrice: 1453,
    currencyCode: 'UYU',
    symbol: '$',
  },
  ES: {
    countryCode: 'ES',
    salePrice: 9.97,
    comparePrice: 37,
    currencyCode: 'EUR',
    symbol: '€',
  },
  US: {
    countryCode: 'US',
    salePrice: 9.97,
    comparePrice: 37,
    currencyCode: 'USD',
    symbol: 'US$',
  },
}

export function formatPrice(value: number, country: string): string {
  const config = PRICING_CONFIG[country] || PRICING_CONFIG['US']
  const { symbol, currencyCode } = config

  let formattedNumber = ''

  if (['CL', 'AR', 'CO', 'UY'].includes(country)) {
    // No decimals, period for thousands
    formattedNumber = Math.round(value)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  } else if (country === 'MX' || country === 'ES') {
    // Comma for decimal, period for thousands
    const parts = value.toFixed(2).split('.')
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    formattedNumber = parts.join(',')
  } else {
    // US and fallback: Period for decimal, comma for thousands
    const parts = value.toFixed(2).split('.')
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    formattedNumber = parts.join('.')
  }

  return `${symbol}${formattedNumber} ${currencyCode}`
}

let cachedGeoPromise: Promise<string> | null = null

async function fetchCountryCode(): Promise<string> {
  if (cachedGeoPromise) return cachedGeoPromise

  cachedGeoPromise = new Promise((resolve) => {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => {
      controller.abort()
      resolve('US')
    }, 3000)

    fetch('https://ipapi.co/json/', { signal: controller.signal })
      .then((res) => res.json())
      .then((data) => {
        clearTimeout(timeoutId)
        resolve(data.country_code || 'US')
      })
      .catch(() => {
        clearTimeout(timeoutId)
        resolve('US')
      })
  })

  return cachedGeoPromise
}

export function useGeoPricing() {
  const [countryCode, setCountryCode] = useState<string>('US')
  const [isLoading, setIsLoading] = useState<boolean>(true)

  useEffect(() => {
    let isMounted = true

    fetchCountryCode().then((code) => {
      if (isMounted) {
        // Only use the detected code if we have a pricing config for it, otherwise fallback to US
        setCountryCode(PRICING_CONFIG[code] ? code : 'US')
        setIsLoading(false)
      }
    })

    return () => {
      isMounted = false
    }
  }, [])

  const pricingData = PRICING_CONFIG[countryCode] || PRICING_CONFIG['US']

  return {
    countryCode,
    currencySymbol: pricingData.symbol,
    currencyCode: pricingData.currencyCode,
    salePrice: pricingData.salePrice,
    comparePrice: pricingData.comparePrice,
    formattedSalePrice: formatPrice(pricingData.salePrice, countryCode),
    formattedComparePrice: formatPrice(pricingData.comparePrice, countryCode),
    isLoading,
  }
}
